<template>
  <div class="flex flex-col h-full">
    <l-option-selector
      v-if="tabs.length > 1"
      v-model="selectedTab"
      :options="tabs"
      class="mb-4 shrink-0"
    />

    <div v-if="selectedTab == 'total'" class="flex-1 flex flex-col">
      <calculator
        class="h-full"
        :initial-to-pay="total"
        keypad-mode="normal"
        display-mode="toPay"
        @change="onChange"
      />
      <l-button
        v-if="showOpenTill && !useCashMachine"
        type="text"
        size="medium"
        class="mt-4"
        @click="openCashDrawer"
      >
        {{ t('start-shift.open-till') }}
      </l-button>
    </div>

    <div v-if="selectedTab === 'billsTotal'" class="flex flex-col flex-1 overflow-hidden">
      <calculator-display
        class="mb-4"
        mode="toPay"
        pending="0"
        change="0"
        :to-pay="currency(billsTotal)"
      />
      <div class="flex-1 overflow-y-scroll scrolling-touch min-h-0">
        <div class="flex flex-col gap-3">
          <div v-for="(bill, index) in bills" :key="bill" class="flex justify-between">
            <div class="font-heading text-n-0 font-medium w-36">
              {{ currency(bill) }}
            </div>
            <l-number-input v-model="billsAmount[index]" format="integer" size="small" :min="0" />
          </div>
        </div>
      </div>
    </div>

    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { LButton, LNumberInput, LOptionSelector, useDialog } from '@last/core-ui/paprika'

import Calculator from '@/components/Calculator/Calculator.vue'
import CalculatorDisplay from '@/components/Calculator/CalculatorDisplay.vue'
import type { CalculatorPayload } from '@/components/Calculator/types'
import { useMoney } from '@/composables/useMoney'
import CashMachine from '@/integrations/cashmachine/cashmachine'
import Printer from '@/integrations/printer/printer'

const props = withDefaults(
  defineProps<{
    modelValue?: number | null
    billsAmounts?: number[] | null
    selectedTab?: string | null
    showBillsCounter?: boolean
    useCashMachine?: boolean
    showOpenTill?: boolean
  }>(),
  {
    modelValue: null,
    billsAmounts: null,
    selectedTab: null,
    showBillsCounter: true,
    useCashMachine: false,
    showOpenTill: false
  }
)
const emit = defineEmits<{
  'update:modelValue': [value: number]
  'update:billsAmounts': [value: number[]]
  'update:selectedTab': [value: string]
}>()

const { t } = useI18n()
const { currency } = useMoney()
const dialog = useDialog()

const tabs = ref<{ label: string; value: string }[]>([])
const selectedTab = ref(props.selectedTab ?? 'total')

const total = ref(props.modelValue ?? 0)
const bills = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000]
const billsAmount = ref(props.billsAmounts ? [...props.billsAmounts] : Array(15))

const billsTotal = computed(() => {
  let total = 0
  bills.forEach((value, index) => {
    total += value * (billsAmount.value[index] ?? 0)
  })
  return total
})

const machineAmount = computed(() => {
  return CashMachine.totalAmount.value
})

onMounted(async () => {
  tabs.value = [
    {
      label: t('cash-amount.total'),
      value: 'total'
    }
  ]
  if (props.showBillsCounter && !props.useCashMachine) {
    tabs.value.push({
      label: t('cash-amount.bills-total'),
      value: 'billsTotal'
    })
  }
  if (props.useCashMachine) {
    let isCanceled = false
    const dialogInstance = dialog({
      icon: 'status',
      iconAnimation: 'animate-spin',
      title: `${t('cash-amount.loading')} ${CashMachine.method}`,
      mainLabel: t('ctas.cancel'),
      onConfirm: () => {
        isCanceled = true
      }
    })
    await CashMachine.refreshTotalAmount()
    if (!isCanceled) {
      total.value = machineAmount.value ?? 0
    }
    dialogInstance?.close()
  }
  if (props.modelValue != null) {
    total.value = props.modelValue
  }
})

function openCashDrawer() {
  Printer.openCashDrawer()
}

function onChange(event: CalculatorPayload) {
  total.value = event.toPay
}

const effectiveAmount = computed(() => {
  return selectedTab.value === 'total' ? total.value : billsTotal.value
})

watch(effectiveAmount, value => {
  emit('update:modelValue', value)
})

watch(billsAmount, value => {
  emit('update:billsAmounts', [...value])
}, { deep: true })

watch(selectedTab, value => {
  emit('update:selectedTab', value)
})

watch(
  () => props.modelValue,
  value => {
    if (value != null && value !== total.value) {
      total.value = value
    }
  }
)
</script>
